/**
 * Calendar Page Styles
 * 
 * Purpose:
 * Shared styles object for the CalendarPage component and its extracted presentation components.
 * Contains all inline style definitions used throughout the calendar page, including hero backdrop,
 * header card, mobile command bar, KPI cards, tourist tax banner, error states, and various
 * UI element styles. All style values are moved verbatim from CalendarPage.jsx to preserve
 * visual behavior exactly.
 * 
 * High-level Workflow:
 * 1. Styles object is exported as a named export for use in CalendarPage.jsx and extracted
 *    presentation components (CalendarPageHeader, CalendarPageMobileCommandBar, etc.).
 * 2. Components receive the styles object as a prop and use style keys directly (e.g., styles.headerTitle).
 * 3. Style functions (e.g., getHeaderCardStyle) are called with parameters (e.g., isMobile, isDocked)
 *    to generate responsive style objects.
 * 4. All style values, keys, and responsive behavior are preserved exactly as they existed in
 *    CalendarPage.jsx before extraction.
 * 
 * Assumptions & Invariants:
 * - Values moved verbatim: All style values, keys, numeric values, colors, spacing, breakpoints,
 *   and layout properties are moved verbatim from CalendarPage.jsx to preserve visual behavior.
 *   No style values were changed during extraction.
 * - Style key names: All style key names are preserved exactly (e.g., getHeaderCardStyle,
 *   mobileCommandBarContent, touristTaxBanner). Components reference these keys by name.
 * - Responsive functions: Style functions that accept parameters (e.g., getHeaderCardStyle(isMobile, isDocked))
 *   are preserved exactly, including all conditional logic and return values.
 * - CSS variables: Styles use CSS custom properties (e.g., var(--spacing-4), var(--color-text-primary))
 *   which are defined in the global CSS/theme system. These variables are assumed to be available
 *   at runtime.
 * - Mobile breakpoint: Mobile-specific styles use isMobile parameter (window.innerWidth <= 768px).
 *   This breakpoint is assumed to match CSS media queries and design system breakpoints.
 * - No style changes: This file contains only the extracted styles object. No style values, keys,
 *   or logic were modified during extraction. This is a mechanical move only.
 * 
 * Dependencies:
 * - Internal:
 *   - None (pure style object, no imports)
 * - External:
 *   - None (pure JavaScript object)
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Initial extraction from CalendarPage.jsx (Phase 6 refactoring). All values moved
 *   verbatim to preserve visual behavior exactly.
 */

// Single source of truth so header card and calendar panel below stay same width/alignment.
export const CALENDAR_CONTAINER_MAX_WIDTH = 'min(1400px, calc(100vw - var(--spacing-6) * 2))';

/** Outer container style (maxWidth, margin, width) shared by header card and calendar panel so they align exactly. */
export function getCalendarOuterContainerStyle(isMobile) {
  return {
    maxWidth: isMobile ? 'none' : CALENDAR_CONTAINER_MAX_WIDTH,
    marginLeft: isMobile ? 0 : 'auto',
    marginRight: isMobile ? 0 : 'auto',
    width: '100%',
    boxSizing: 'border-box',
    minWidth: 0,
  };
}

export const styles = {
  // Hero Backdrop - Fixed background layer that persists behind content
  getHeroBackdropStyle: (isMobile) => ({
    position: 'fixed', // Fixed positioning relative to viewport (persists on scroll)
    top: 'var(--owner-top-nav-height, 52px)', // Start below global nav
    left: 0,
    right: 0,
    // PHASE 6B: Reduced height on mobile - just behind command bar + minimal context
    height: isMobile ? '140px' : '800px', // Mobile: 140px (command bar ~52px + property card ~88px), Desktop: 800px
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    zIndex: 0, // Behind all content
    // Premium neutral gradient fallback (when no hero image)
    backgroundColor: '#f8f9fa', // Fallback color if gradient doesn't load
    pointerEvents: 'none', // Don't block interactions
  }),
  heroBackdropFade: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // Vertical fade overlay: transitions from transparent at top to neutral at bottom
    // Refined: lighter, more atmospheric fade (reduced opacity stops for subtlety)
    // Before: 0% → 30% → 80% → 100%, After: 0% → 20% → 70% → 100%
    background: 'linear-gradient(180deg, rgba(248, 249, 250, 0) 0%, rgba(248, 249, 250, 0.2) 50%, rgba(248, 249, 250, 0.7) 80%, rgba(248, 249, 250, 1) 100%)',
    zIndex: 1,
    pointerEvents: 'none', // Don't block interactions
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.25) 50%, rgba(0, 0, 0, 0.0) 100%)',
    // Overlay only shown when hero image is loaded (for text readability)
    zIndex: 2, // Above backdrop, below fade
  },
  // Header Card - Scroll-activated sticky property navigation bar with glass styling
  // PHASE 1: Canonical container pattern - prevents horizontal overflow
  // PHASE 9A: Edge-to-edge mobile surface - remove centered container behavior on mobile
  getHeaderCardStyle: (isMobile, isDocked) => {
    const baseStyle = {
      ...getCalendarOuterContainerStyle(isMobile),
      zIndex: 150, // Below global nav (200/sticky) but above content
      marginBottom: '8px', // Single source of truth: --calendar-section-gap (8px)
      // Desktop: no horizontal padding so card width aligns with panel. Mobile: 16px inline.
      padding: isMobile ? '0 var(--spacing-4)' : 0,
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', // Premium easing: smooth but not sluggish
    };

    if (isDocked) {
      // Docked state: sticky at top with gap below nav
      // Docking threshold: header card scrolls past the sentinel position, then sticks
      return {
        ...baseStyle,
        position: 'sticky',
        top: 'calc(var(--owner-top-nav-height, 52px) + var(--owner-sticky-gap, 12px))', // Position below global nav with 12px gap (64px total)
        marginTop: 0, // No negative margin when docked
      };
    } else {
      // Undocked state: relative positioning, visually placed in hero backdrop
      // Tighter spacing for better vertical rhythm (24px/32px follows spacing scale)
      return {
        ...baseStyle,
        position: 'relative',
        marginTop: '8px', // Single source of truth: --calendar-section-gap (8px)
      };
    }
  },
  // PHASE 9C: Property context - Inline metadata (remove card container chrome)
  getHeaderCardContentStyle: (isMobile) => ({
    backgroundColor: isMobile ? 'transparent' : 'var(--owner-header-card-bg, rgba(255, 255, 255, 0.75))', // PHASE 9C: No background on mobile (inline metadata)
    backdropFilter: isMobile ? 'none' : 'blur(12px)', // PHASE 9C: No glass effect on mobile
    WebkitBackdropFilter: isMobile ? 'none' : 'blur(12px)', // PHASE 9C: No glass effect on mobile
    borderRadius: isMobile ? '0' : 'var(--radius-lg, 0.5rem)', // PHASE 9C: No border radius on mobile
    // PHASE 9C: Minimal padding on mobile (8px vertical - inline metadata)
    padding: isMobile ? 'var(--spacing-1) var(--spacing-3)' : 'var(--spacing-3) var(--spacing-4)', // PHASE 9C: 8px vertical padding on mobile
    borderBottom: isMobile ? 'none' : '1px solid var(--owner-header-card-border, rgba(0, 0, 0, 0.08))', // PHASE 9C: No border on mobile
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row', // PHASE 3: Stack on mobile
    alignItems: isMobile ? 'stretch' : 'flex-start', // PHASE 3: Stretch on mobile for full-width controls
    justifyContent: 'space-between',
    gap: isMobile ? 'var(--spacing-1)' : 'var(--spacing-3)', // PHASE 6B: Tighter gap on mobile (8px vs 24px)
    // Note: Browsers without backdrop-filter will show more opaque rgba background (graceful degradation)
  }),
  // Desktop-only: inner glass wrapper (flex row: image column + content column). Match OwnerPageChrome header card layout.
  getHeaderCardWrapperStyle: (isMobile) => (isMobile ? {} : {
    backgroundColor: 'var(--owner-header-card-bg, rgba(255, 255, 255, 0.75))',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: 'var(--radius-lg, 0.5rem)',
    borderBottom: '1px solid var(--owner-header-card-border, rgba(0, 0, 0, 0.08))',
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    minHeight: 0,
  }),
  getHeaderImageColumnStyle: (isMobile) => (isMobile ? {} : {
    width: 120,
    minWidth: 120,
    flexShrink: 0,
    alignSelf: 'stretch',
    position: 'relative',
    overflow: 'hidden',
    borderTopLeftRadius: 'var(--radius-lg, 0.5rem)',
    borderBottomLeftRadius: 'var(--radius-lg, 0.5rem)',
    backgroundColor: 'var(--color-bg-secondary)',
  }),
  headerCardImage: {
    width: '100%',
    height: '100%',
    minHeight: '100%',
    objectFit: 'cover',
    display: 'block',
    verticalAlign: 'middle',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  getHeaderContentColumnStyle: (isMobile) => (isMobile ? {} : {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 'var(--spacing-3)',
    padding: 'var(--spacing-3) var(--spacing-4)',
  }),
  headerLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--spacing-4)',
    flex: '1 1 auto',
    minWidth: 0,
  },
  avatarContainer: {
    width: '56px',
    height: '56px',
    borderRadius: 'var(--radius-md, 0.375rem)',
    overflow: 'hidden',
    backgroundColor: 'var(--color-bg-secondary)',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid var(--color-border-light, #e5e7eb)',
  },
  // PHASE 6B: Mobile avatar container - smaller on mobile
  getAvatarContainerMobileStyle: () => ({
    width: '40px', // Reduced from 56px
    height: '40px', // Reduced from 56px
    borderRadius: 'var(--radius-md, 0.375rem)',
    overflow: 'hidden',
    backgroundColor: 'var(--color-bg-secondary)',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid var(--color-border-light, #e5e7eb)',
  }),
  avatarImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    flexShrink: 0,
  },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.4,
    display: 'flex',
    color: 'var(--color-text-secondary)',
  },
  headerTitleSection: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  headerTitle: {
    fontSize: 'var(--font-size-2xl, 1.5rem)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-text-primary, #111827)',
    margin: 0,
    marginBottom: 'var(--spacing-1)',
    cursor: 'pointer',
    textDecoration: 'underline',
    textDecorationColor: 'transparent',
    transition: 'text-decoration-color 0.2s ease',
  },
  // PHASE 6B: Mobile header title - max 2 lines, smaller font
  getHeaderTitleMobileStyle: () => ({
    fontSize: 'var(--font-size-base, 1rem)', // Reduced from 2xl (1.5rem)
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-text-primary, #111827)',
    margin: 0,
    marginBottom: 0, // No margin bottom on mobile (subtitle hidden)
    cursor: 'pointer',
    textDecoration: 'underline',
    textDecorationColor: 'transparent',
    transition: 'text-decoration-color 0.2s ease',
    lineHeight: '1.3', // Tighter line height for compact display
    maxHeight: '2.6em', // Max 2 lines (1.3em * 2)
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2, // Limit to 2 lines
    WebkitBoxOrient: 'vertical',
  }),
  headerSubtitle: {
    fontSize: 'var(--font-size-sm, 0.875rem)',
    color: 'var(--color-text-secondary, #6b7280)',
    margin: 0,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-3)',
    flexShrink: 0,
  },
  clickableTitle: {
    cursor: 'pointer',
    textDecoration: 'underline',
    textDecorationColor: 'transparent',
    transition: 'text-decoration-color 0.2s ease',
  },
  container: {
    padding: 'var(--spacing-6)',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  // PHASE 1: Canonical container pattern - prevents horizontal overflow
  // PHASE 8: Optimized mobile density - tighter padding
  // PHASE 9C: Calendar - Surface, not card (minimal padding, no chrome)
  getCalendarPanelStyle: (isMobile) => ({
    ...getCalendarOuterContainerStyle(isMobile),
    marginTop: isMobile ? '-16px' : '-8px', // PHASE 9C: More negative margin (tighter connection)
    marginBottom: '8px', // Single source of truth: --calendar-section-gap (8px) - KPI has marginTop: 0
    backgroundColor: 'var(--owner-card-bg)', // Use unified card background
    // Desktop: no horizontal padding so visible calendar card aligns with header card (same width).
    // Mobile: keep horizontal padding for edge spacing.
    padding: isMobile ? 'var(--spacing-1) var(--spacing-4)' : 'var(--spacing-6) 0', // vertical only on desktop
    overflowX: isMobile ? 'hidden' : undefined,
    // PHASE 9A: Mobile-native surface - reduced/removed shadow and radius on mobile
    borderRadius: isMobile ? '0' : 'var(--owner-card-radius)', // PHASE 9A: No border radius on mobile (edge-to-edge)
    boxShadow: isMobile ? 'none' : 'var(--owner-card-shadow)', // PHASE 9A: No shadow on mobile (surface, not card)
    border: isMobile ? 'none' : 'var(--owner-card-border)', // PHASE 9A: No border on mobile
    position: 'relative',
    zIndex: 20, // Above hero backdrop and overlays (increased from 10)
  }),
  // PHASE 9B: Global density pass - reduced vertical spacing ~25% on mobile
  getKPIContainerStyle: (isMobile) => ({
    position: 'relative',
    zIndex: 20, // Above hero backdrop and overlays
    marginTop: '0', // Calendar panel owns the gap (marginBottom: 8px) - prevent stacking
    marginBottom: isMobile ? '8px' : 'var(--spacing-3)', // PHASE 11: 8px micro gap on mobile (was 8px, keeping consistent)
    // PHASE 9A: Remove centered container behavior on mobile (maxWidth + margin auto)
    maxWidth: isMobile ? 'none' : 'min(1400px, calc(100vw - var(--spacing-6) * 2))',
    marginLeft: isMobile ? 0 : 'auto',
    marginRight: isMobile ? 0 : 'auto',
    // PHASE 9A: Mobile spacing system - consistent padding-inline (16px)
    padding: isMobile ? 'var(--spacing-3) var(--spacing-4)' : '0 var(--spacing-6)', // PHASE 9A: 16px horizontal padding on mobile
    width: '100%',
    boxSizing: 'border-box',
    minWidth: 0, // Allow flex children to shrink
    // Mobile: card-style panel so section has clear background and separation
    ...(isMobile ? {
      backgroundColor: 'var(--color-bg-primary, #ffffff)',
      border: '1px solid var(--color-border-light, #e5e7eb)',
      borderRadius: 'var(--radius-md, 8px)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    } : {}),
  }),
  calendarSkeleton: {
    minHeight: '400px',
  },
  // PHASE 1: Canonical container pattern - prevents horizontal overflow
  // PHASE 9A: Edge-to-edge mobile surface - remove centered container behavior on mobile
  getErrorBannerStyle: (isMobile) => ({
    // PHASE 9A: Remove centered container behavior on mobile (maxWidth + margin auto)
    maxWidth: isMobile ? 'none' : 'min(1400px, calc(100vw - var(--spacing-6) * 2))',
    marginLeft: isMobile ? 0 : 'auto',
    marginRight: isMobile ? 0 : 'auto',
    // PHASE 9A: Mobile spacing system - consistent padding-inline (16px)
    padding: isMobile ? '0 var(--spacing-4)' : '0 var(--spacing-6)', // PHASE 9A: 16px horizontal padding on mobile
    width: '100%',
    boxSizing: 'border-box',
    minWidth: 0, // Allow flex children to shrink
    marginBottom: 'var(--spacing-4)',
  }),
  // PHASE 9B: Global density pass - reduced vertical spacing ~25% on mobile (sections feel connected)
  // PHASE 11: Further density optimization - 20-30% vertical space reduction
  getBelowCalendarStyle: (isMobile) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: isMobile ? '8px' : 'var(--spacing-4)', // PHASE 11: 8px micro gap on mobile (was 16px)
    marginBottom: isMobile ? '12px' : 'var(--spacing-6)', // PHASE 11: Max 12px section break (was 24px)
    position: 'relative',
    zIndex: 20, // Above hero backdrop and overlays
    // PHASE 9A: Remove centered container behavior on mobile (maxWidth + margin auto)
    maxWidth: isMobile ? 'none' : 'min(1400px, calc(100vw - var(--spacing-6) * 2))',
    marginLeft: isMobile ? 0 : 'auto',
    marginRight: isMobile ? 0 : 'auto',
    // PHASE 9A: Mobile spacing system - consistent padding-inline (16px)
    padding: isMobile ? '0 var(--spacing-4)' : '0 var(--spacing-6)', // PHASE 9A: 16px horizontal padding on mobile
    width: '100%',
    boxSizing: 'border-box',
    minWidth: 0, // Allow flex children to shrink
  }),
  fullWidthColumn: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    width: '100%',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  eventFilters: {
    display: 'flex',
    gap: 'var(--spacing-4)',
    padding: 'var(--spacing-3)',
    backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
    borderRadius: 'var(--radius-md, 0.375rem)',
  },
  filterLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
  },
  checkbox: {
    cursor: 'pointer',
  },
  periodSummary: {
    display: 'flex',
    gap: 'var(--spacing-4)',
    padding: 'var(--spacing-4)',
    backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
    borderRadius: 'var(--radius-md)',
    marginBottom: 'var(--spacing-6)',
    flexWrap: 'wrap',
    border: '1px solid var(--color-border-light, #e5e7eb)',
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
    minWidth: '120px',
  },
  summaryLabel: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary, #6b7280)',
    fontWeight: 'var(--font-weight-medium)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  summaryValue: {
    fontSize: 'var(--font-size-base)',
    color: 'var(--color-text-primary, #111827)',
    fontWeight: 'var(--font-weight-semibold)',
  },
  // PHASE 5: Mobile Command Bar styles, PHASE 6B: Strengthened visual hierarchy
  // PHASE 9C: Command bar - System-level chrome (hairline divider only, no boxed appearance)
  // PHASE 11: Further density optimization (6px vertical padding)
  getMobileCommandBarStyle: () => ({
    position: 'sticky',
    top: 'var(--owner-top-nav-height, 52px)',
    zIndex: 100,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid var(--color-border-light)',
    boxShadow: 'none',
    padding: '6px var(--spacing-4)',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    boxSizing: 'border-box',
  }),
  /* minWidth: min-content so bar content can overflow and scroll horizontally (Today stays visible) */
  mobileCommandBarContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--spacing-2)',
    minWidth: 'min-content',
    maxWidth: 'none',
    width: '100%',
    margin: 0,
    boxSizing: 'border-box',
  },
  // PHASE 9B: Mobile Command Bar - Month nav grouped (‹ Month › reads as ONE control)
  // PHASE 11: Enhanced with Today button, tighter spacing
  // B2: One-thumb navigation cluster - grouped container
  mobileCommandBarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    flex: '1 1 auto',
    minWidth: 0,
    justifyContent: 'flex-start',
  },
  // B2: Navigation cluster - can shrink on very narrow screens
  mobileNavCluster: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: 'var(--spacing-1)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
    border: '1px solid var(--color-border-light)',
    flexShrink: 1,
    minWidth: 0,
  },
  mobilePropertyName: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  mobileSwitchButton: {
    padding: 'var(--spacing-1) var(--spacing-2)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-brand-primary)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-brand-primary)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    minHeight: '32px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: 'var(--transition-base)',
  },
  // PHASE 9B: Property button SECONDARY (visual weight reduced, does not compete with month nav)
  // PHASE 11: Tighter spacing, no wrapping
  mobileCommandBarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px', // PHASE 11: 6px micro gap (was 8px)
    flexShrink: 0,
    // PHASE 9B: Property button visually secondary to month navigation (icon-only, lighter weight)
    // PHASE 11: Property name truncates, never wraps
  },
  // PHASE 9B: Nav buttons grouped with month label (reads as ONE control: ‹ Month ›)
  mobileNavButton: {
    padding: 'var(--spacing-1) var(--spacing-2)', // PHASE 9B: Tighter padding (grouped control)
    fontSize: 'var(--font-size-lg)', // PHASE 9B: Larger chevrons for grouped control clarity
    fontWeight: 'var(--font-weight-normal)',
    color: 'var(--color-text-primary)', // PHASE 9B: Primary color (same as month label)
    backgroundColor: 'transparent',
    border: '1px solid var(--color-border-light)', // PHASE 9B: Lighter border (grouped feel)
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    minWidth: '44px', // PHASE 9B: Minimum touch target
    minHeight: '44px', // PHASE 9B: Minimum touch target
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'var(--transition-base)',
    lineHeight: 1,
    flexShrink: 0, // PHASE 9B: Don't shrink (fixed width buttons)
  },
  // PHASE 11: Today button - discoverable and mobile-appropriate
  mobileTodayButton: {
    padding: 'var(--spacing-1) var(--spacing-2)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-brand-primary)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-brand-primary)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    minWidth: '44px', // PHASE 11: Minimum touch target
    minHeight: '44px', // PHASE 11: Minimum touch target
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'var(--transition-base)',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    marginLeft: 'var(--spacing-1)', // PHASE 11: Tighter gap after month label
  },
  // B3: Legend button - mobile-only, within nav cluster
  mobileLegendButton: {
    padding: 'var(--spacing-1) var(--spacing-2)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    minWidth: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-1)',
    transition: 'var(--transition-base)',
    flexShrink: 0,
  },
  legendIcon: {
    fontSize: 'var(--font-size-base)',
    lineHeight: 1,
  },
  legendLabel: {
    fontSize: 'var(--font-size-xs)',
    whiteSpace: 'nowrap',
  },
  // PHASE 9B: Month/Year label visually PRIMARY (larger, bolder, grouped with nav buttons)
  // PHASE 11: Truncation with ellipsis (never wraps)
  mobileMonthLabel: {
    fontSize: 'var(--font-size-lg)', // PHASE 9B: Larger font (was base) - PRIMARY hierarchy
    fontWeight: 'var(--font-weight-semibold)', // PHASE 9B: Bolder for authority
    color: 'var(--color-text-primary)',
    whiteSpace: 'nowrap', // Never wrap
    overflow: 'hidden', // Prevent overflow
    textOverflow: 'ellipsis', // Truncate with ellipsis
    minWidth: 0,
    maxWidth: '180px', // PHASE 11: Reasonable max width for truncation
    // PHASE 11: Truncation ensures no wrapping at 360px
    textAlign: 'center',
    padding: '0 var(--spacing-2)', // Visual breathing room within grouped control
    flex: '1 1 auto', // PHASE 9B: Take available space (prioritize month label)
    lineHeight: 1.2, // PHASE 9B: Tighter line height for mobile density
  },
  // PHASE 9B: Property button SECONDARY (icon-only or minimal label) - does not compete with month nav
  mobilePropertyButton: {
    padding: 'var(--spacing-1) var(--spacing-2)', // PHASE 9B: Tighter padding (secondary)
    fontSize: 'var(--font-size-base)', // PHASE 9B: Icon size (label hidden on small screens)
    fontWeight: 'var(--font-weight-normal)', // PHASE 9B: Lighter weight (secondary)
    color: 'var(--color-text-secondary)', // PHASE 9B: Secondary color (not primary)
    backgroundColor: 'transparent',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-sm)', // PHASE 9B: Smaller radius (secondary)
    cursor: 'pointer',
    minWidth: '44px', // PHASE 9B: Minimum touch target
    minHeight: '44px', // PHASE 9B: Minimum touch target
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: 'var(--transition-base)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center', // PHASE 9B: Center icon
    gap: 'var(--spacing-1)',
  },
  mobilePropertyButtonIcon: {
    fontSize: 'var(--font-size-base)', // PHASE 9B: Icon size
    lineHeight: 1,
    flexShrink: 0,
  },
  // PHASE 9B: Property label hidden on small screens (icon-only), shown on larger mobile if space
  mobilePropertyButtonLabel: {
    fontSize: 'var(--font-size-xs)', // PHASE 9B: Smaller label (secondary)
    display: 'none', // PHASE 9B: Hidden by default (icon-only) - can show at >430px if needed
  },
  // PHASE 5: Mobile BottomSheet content styles
  mobileSheetContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-4)',
  },
  mobileSheetSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-2)',
  },
  mobileSheetSectionTitle: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    margin: 0,
  },
  mobileLinksList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
  },
  mobileLinkButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-3)',
    padding: 'var(--spacing-3) var(--spacing-4)',
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    minHeight: '44px', // Touch target minimum
    textAlign: 'left',
    transition: 'var(--transition-base)',
  },
  mobileLinkIcon: {
    fontSize: 'var(--font-size-base)',
    width: '24px',
    textAlign: 'center',
    flexShrink: 0,
  },
  mobileLinkLabel: {
    flex: '1 1 auto',
  },
  mobileLinkArrow: {
    fontSize: 'var(--font-size-xs)',
    opacity: 0.6,
    flexShrink: 0,
  },
  mobileSheetEmpty: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic',
    margin: 0,
    padding: 'var(--spacing-4)',
  },
  // PHASE 6A: Mobile Property Switcher styles
  mobilePropertySwitcherList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
  },
  mobilePropertySwitcherButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'var(--spacing-3) var(--spacing-4)',
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    minHeight: '44px', // Touch target minimum
    textAlign: 'left',
    transition: 'var(--transition-base)',
    width: '100%',
  },
  mobilePropertySwitcherButtonSelected: {
    backgroundColor: 'var(--color-bg-secondary)',
    borderColor: 'var(--color-brand-primary)',
    fontWeight: 'var(--font-weight-semibold)',
  },
  mobilePropertySwitcherButtonLabel: {
    flex: '1 1 auto',
  },
  mobilePropertyButtonCheck: {
    fontSize: 'var(--font-size-lg)',
    color: 'var(--color-brand-primary)',
    flexShrink: 0,
    marginLeft: 'var(--spacing-2)',
  },
  // PHASE 6B: Mobile Display button (Hide calendar in BottomSheet)
  mobileDisplayButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'var(--spacing-3) var(--spacing-4)',
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    minHeight: '44px', // Touch target minimum
    textAlign: 'left',
    transition: 'var(--transition-base)',
    width: '100%',
  },
  mobileDisplayButtonLabel: {
    flex: '1 1 auto',
  },
  mobileDisplayButtonIcon: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    flexShrink: 0,
    marginLeft: 'var(--spacing-2)',
  },
  // PHASE 7A: KPI expand/collapse container
  kpiExpandContainer: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 'var(--spacing-3)',
    paddingBottom: 'var(--spacing-2)',
  },
  // BEYOND WORLD-CLASS: Trust-grade status notice (replaces vague error messages)
  statusNotice: {
    padding: 'var(--spacing-3) var(--spacing-4)',
    backgroundColor: 'var(--color-bg-secondary)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    marginBottom: 'var(--spacing-2)',
  },
  statusNoticeHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    marginBottom: 'var(--spacing-2)',
  },
  statusNoticeIcon: {
    fontSize: 'var(--font-size-base)',
    flexShrink: 0,
  },
  statusNoticeTitle: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
  },
  statusNoticeBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-2)',
  },
  statusNoticeText: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.4,
    margin: 0,
  },
  statusNoticeAction: {
    padding: 'var(--spacing-2) var(--spacing-3)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-bg-primary)',
    border: '1px solid var(--color-border-medium)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    alignSelf: 'flex-start',
    minHeight: '36px',
    transition: 'background-color 120ms ease-out',
  },
  // Tourist tax banner styles
  touristTaxBanner: {
    backgroundColor: 'var(--color-status-warning-bg, #fef3c7)',
    border: '1px solid var(--color-status-warning-base, #f59e0b)',
    borderRadius: 'var(--owner-card-radius, 8px)',
    padding: 'var(--spacing-4)',
    marginTop: 'var(--spacing-4)',
    position: 'relative',
    zIndex: 20, // Above hero backdrop and overlays
  },
  getTouristTaxBannerStyleMobile: () => ({
    backgroundColor: 'var(--color-status-warning-bg, #fef3c7)',
    border: '1px solid var(--color-status-warning-base, #f59e0b)',
    borderRadius: 'var(--owner-card-radius, 8px)',
    padding: 'var(--spacing-3)',
    marginTop: 'var(--spacing-3)',
    position: 'relative',
    zIndex: 20,
  }),
  touristTaxBannerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--spacing-4)',
    flexWrap: 'wrap',
  },
  getTouristTaxBannerContentMobile: () => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 'var(--spacing-3)',
    flexWrap: 'wrap',
    flexDirection: 'column',
  }),
  touristTaxBannerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
    flex: '1 1 auto',
    minWidth: 0,
  },
  touristTaxBannerTitle: {
    fontSize: 'var(--font-size-base, 1rem)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-status-warning-text, #92400e)',
    margin: 0,
  },
  getTouristTaxBannerTitleMobile: () => ({
    fontSize: 'var(--font-size-sm, 0.875rem)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-status-warning-text, #92400e)',
    margin: 0,
  }),
  touristTaxBannerSubtext: {
    fontSize: 'var(--font-size-sm, 0.875rem)',
    color: 'var(--color-status-warning-text, #92400e)',
    opacity: 0.8,
    margin: 0,
  },
  getTouristTaxBannerSubtextMobile: () => ({
    fontSize: 'var(--font-size-xs, 0.75rem)',
    color: 'var(--color-status-warning-text, #92400e)',
    opacity: 0.8,
    margin: 0,
  }),
  touristTaxBannerButton: {
    padding: 'var(--spacing-2) var(--spacing-4)',
    fontSize: 'var(--font-size-sm, 0.875rem)',
    fontWeight: 'var(--font-weight-medium, 500)',
    color: 'var(--color-status-warning-text, #92400e)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-status-warning-base, #f59e0b)',
    borderRadius: 'var(--radius-sm, 4px)',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'background-color 120ms ease-out, border-color 120ms ease-out',
    whiteSpace: 'nowrap',
  },
  getTouristTaxBannerButtonMobile: () => ({
    padding: 'var(--spacing-2) var(--spacing-3)',
    fontSize: 'var(--font-size-sm, 0.875rem)',
    fontWeight: 'var(--font-weight-medium, 500)',
    color: 'var(--color-status-warning-text, #92400e)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-status-warning-base, #f59e0b)',
    borderRadius: 'var(--radius-sm, 4px)',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'background-color 120ms ease-out, border-color 120ms ease-out',
    whiteSpace: 'nowrap',
    alignSelf: 'flex-start',
  }),
  // PHASE 11: Subtle error message - quiet and non-disruptive (kept for backward compatibility)
  subtleErrorMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px var(--spacing-3)',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-tertiary)',
    backgroundColor: 'rgba(239, 68, 68, 0.05)',
    borderLeft: '2px solid var(--color-status-error-base)',
    marginBottom: '8px',
    borderRadius: 'var(--radius-sm)',
  },
  subtleErrorIcon: {
    fontSize: 'var(--font-size-xs)',
    flexShrink: 0,
  },
  subtleErrorText: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-tertiary)',
  },
  // PHASE 9D: KPI expand button - Touch feedback and smooth transitions
  kpiExpandButton: {
    padding: 'var(--spacing-2) var(--spacing-4)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    minHeight: '44px', // PHASE 7A: Touch target
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    // PHASE 9D: Fast, subtle transitions (80ms for touch feedback)
    transition: 'transform 80ms ease-out, opacity 80ms ease-out, background-color 120ms ease-out',
    WebkitTapHighlightColor: 'transparent', // PHASE 9D: Remove default tap highlight
  },
  kpiExpandIcon: {
    fontSize: 'var(--font-size-xs)',
    lineHeight: 1,
  },
};
